import { useMemo } from "react";
import { Box, Stack, Typography } from "@mui/material";

// Colores para áreas (mismo esquema que HorariosTable)
const AREA_COLORS = [
  {
    main: "#34C759",
    soft: "rgba(52,199,89,0.22)",
    border: "rgba(52,199,89,0.55)",
  },
  {
    main: "#16B8C7",
    soft: "rgba(22,184,199,0.22)",
    border: "rgba(22,184,199,0.55)",
  },
  {
    main: "#C13CE3",
    soft: "rgba(193,60,227,0.22)",
    border: "rgba(193,60,227,0.55)",
  },
  {
    main: "#F59E0B",
    soft: "rgba(245,158,11,0.22)",
    border: "rgba(245,158,11,0.55)",
  },
  {
    main: "#6366F1",
    soft: "rgba(99,102,241,0.22)",
    border: "rgba(99,102,241,0.55)",
  },
  {
    main: "#EC4899",
    soft: "rgba(236,72,153,0.20)",
    border: "rgba(236,72,153,0.52)",
  },
  {
    main: "#10B981",
    soft: "rgba(16,185,129,0.20)",
    border: "rgba(16,185,129,0.52)",
  },
  {
    main: "#EF4444",
    soft: "rgba(239,68,68,0.18)",
    border: "rgba(239,68,68,0.50)",
  },
  {
    main: "#8B5CF6",
    soft: "rgba(139,92,246,0.20)",
    border: "rgba(139,92,246,0.52)",
  },
  {
    main: "#06B6D4",
    soft: "rgba(6,182,212,0.20)",
    border: "rgba(6,182,212,0.52)",
  },
];

function getAreaColor(areaId) {
  const id = Number(areaId || 0);
  if (!id) return AREA_COLORS[0];
  return AREA_COLORS[Math.abs(id) % AREA_COLORS.length];
}

function normalizeId(value) {
  return value === null || value === undefined || value === "" ? "" : String(value);
}

/**
 * Agrupa filas de asistencia por área
 * @param {Array} rows - Array de registros de asistencia con areaId, fullName, etc.
 * @returns {Array} Array de secciones agrupadas por área
 */
function buildAreaSections(rows = []) {
  const sectionMap = new Map();

  const ensureSection = (area) => {
    const areaId = normalizeId(area?.id) || "sin-area";
    const areaName = area?.name || (areaId === "sin-area" ? "Sin área" : `Área ${areaId}`);

    if (!sectionMap.has(areaId)) {
      sectionMap.set(areaId, {
        areaId,
        areaName,
        color: getAreaColor(areaId),
        items: [],
      });
    }

    return sectionMap.get(areaId);
  };

  for (const row of rows) {
    const areaId = normalizeId(row?.areaId) || "sin-area";
    const areaName = row?.areaName || (areaId === "sin-area" ? "Sin área" : `Área ${areaId}`);

    const section = ensureSection({ id: areaId, name: areaName });
    section.items.push(row);
  }

  return Array.from(sectionMap.values());
}

/**
 * Componente que renderiza las asistencias agrupadas por área
 */
export default function AttendanceGroupedByArea({ rows = [], renderRow = null }) {
  const sections = useMemo(() => buildAreaSections(rows), [rows]);

  if (sections.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
        <Typography variant="body2">Sin registros de asistencia</Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={0}>
      {sections.map((section) => (
        <Box
          key={section.areaId}
          sx={{
            mt: 1,
            borderRadius: 2,
            overflow: "hidden",
            backgroundColor: section.color.soft,
            border: `1px solid ${section.color.border}`,
            borderLeft: `4px solid ${section.color.main}`,
          }}
        >
          {/* Encabezado del área */}
          <Box
            sx={{
              backgroundColor: "transparent",
              borderBottom: `1px solid ${section.color.border}`,
              px: 2,
              py: 1.5,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: section.color.main,
              }}
            >
              {section.areaName}
            </Typography>
          </Box>

          {/* Items del área */}
          <Stack spacing={0}>
            {section.items.map((row, idx) => (
              <Box
                key={`${row.employeeId}-${idx}`}
                sx={{
                  backgroundColor: section.color.soft,
                  borderBottom: `1px solid rgba(0,0,0,0.08)`,
                  "&:last-child": {
                    borderBottom: "none",
                  },
                }}
              >
                {renderRow ? renderRow(row, section) : <DefaultRowRenderer row={row} />}
              </Box>
            ))}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

/**
 * Renderizador de fila por defecto
 */
function DefaultRowRenderer({ row }) {
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="body2">
        {row.fullName} ({row.dni})
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {row.firstClockInAt ? new Date(row.firstClockInAt).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" }) : "-"}
        {" - "}
        {row.lastClockOutAt ? new Date(row.lastClockOutAt).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" }) : "-"}
      </Typography>
    </Box>
  );
}
